/**
 * Product catalogue for all cylindrical configurators.
 *
 * Every size is in millimetres. A container is described by its outline
 * profile (radius at each height, rim at y = 0) plus the printable band,
 * which must lie on a constant-radius part of the body. Artwork placement is
 * stored on the unwrapped band, never in screen pixels.
 *
 * Every item must fit the printer: body Ø 45–160 mm, length 80–280 mm (see LIMITS).
 * Only round, handleless items are offered.
 *
 * TODO(content): sizes are representative placeholders. Replace them with
 * RDK's real catalogue (and supplier SKUs) before launch.
 */
import { PRINT_LIMITS, type Coverage, type InkSetup, type MaterialId, type ProductKind } from '@shared/rfq';

export type SingleProductKind = Exclude<ProductKind, 'gift'>;
/** [y from rim, radius] pairs, top to bottom. */
export type Profile = [number, number][];

export type ClosureKind = 'lid' | 'cap' | 'cork' | 'dropper' | 'pump' | 'reeds' | 'thermo' | 'sport' | 'swing' | 'tcork';
export type ClosureFinish = 'wood' | 'gold' | 'silver' | 'black' | 'white' | 'cork' | 'bamboo';

export interface ContainerModel {
  id: string;
  product: SingleProductKind;
  name: string;
  /** Body (print) diameter. */
  diameterMm: number;
  /** Rim to bottom, without closure. */
  heightMm: number;
  volumeMl: number;
  profile: Profile;
  print: { topMm: number; heightMm: number };
  contents: { kind: 'wax' | 'liquid' | 'cream' | 'none'; topMm: number; tint?: string };
  closure?: { kind: ClosureKind; heightMm: number; finish: ClosureFinish };
  /** Legacy (mugs). The current catalogue is round and handleless only. */
  handle?: { widthMm: number };
  /** Physical glass/metal details used by the renderer; defaults per product. */
  build?: { baseMm?: number; wallMm?: number; puntMm?: number };
  /** Areas of the band that cannot be printed (e.g. mug handle), centre u and width. */
  noPrint?: { uMm: number; widthMm: number }[];
  materials: MaterialId[];
  defaultMaterial: MaterialId;
}

// ------------------------------------------------------------------ profile helpers
const smooth = (t: number) => (1 - Math.cos(Math.PI * t)) / 2;

/** Quarter-round of radius b at the bottom edge of a body of radius r ending at h. */
function roundedBase(h: number, r: number, b: number): Profile {
  const p: Profile = [];
  for (let i = 1; i <= 6; i++) {
    const t = (i / 6) * (Math.PI / 2);
    p.push([h - b + b * Math.sin(t), r - b + b * Math.cos(t) * 0.55 + b * 0.45]);
  }
  return p;
}

/** Straight tumbler: fire-polished rim bead, straight wall, softly rounded heel. */
function straight(h: number, r: number): Profile {
  const b = Math.min(5, r * 0.15);
  return [[0, r - 0.5], [0.6, r - 0.1], [1.5, r], [h - b, r], ...roundedBase(h, r, b)];
}

/**
 * Vacuum flask / steel bottle: threaded neck slightly narrower than the body,
 * a short rounded shoulder, straight wall and a pressed base with a small chamfer.
 */
function flask(h: number, r: number, rn: number, neckLen: number, shoulderEnd: number): Profile {
  const p: Profile = [[0, rn - 0.4], [0.5, rn], [neckLen, rn]];
  const steps = 10;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    p.push([neckLen + (shoulderEnd - neckLen) * t, rn + (r - rn) * Math.sin((Math.PI / 2) * t)]);
  }
  // seam of the vacuum base cup
  p.push([h - 7, r], [h - 6.4, r - 0.35], [h - 5.8, r], [h - 2.2, r], [h - 0.8, r - 0.9], [h, r - 2.2]);
  return p;
}

/** Bottle: finish ring, neck rn × neckLen, curved shoulder to body radius r at shoulderEnd, rounded heel. */
function bottle(h: number, r: number, rn: number, neckLen: number, shoulderEnd: number, lip = 1.2): Profile {
  const lipH = Math.max(3, Math.min(9, neckLen * 0.45));
  const p: Profile = [
    [0, rn + lip - 0.6],
    [0.6, rn + lip],
    [lipH - 0.8, rn + lip],
    [lipH, rn + lip * 0.4],
    [lipH + 0.8, rn],
    [neckLen, rn],
  ];
  const steps = 16;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    p.push([neckLen + (shoulderEnd - neckLen) * t, rn + (r - rn) * smooth(t)]);
  }
  const b = Math.min(4.5, r * 0.14);
  p.push([h - b, r], ...roundedBase(h, r, b));
  return p;
}

/** Swing-top bottle: bead finish for the wire bail, short neck, round shoulder. */
function swingProfile(h: number, r: number): Profile {
  const rn = 13.2;
  const p: Profile = [
    [0, 13.4], [0.8, 14.6], [3.2, 14.8], [4.4, 13.6], [9, 13.4], [10.2, 15.2], [12.4, 15.3], [13.6, 13.6], [34, rn + 0.6],
  ];
  const s0 = 34;
  const s1 = 82;
  for (let i = 1; i <= 20; i++) {
    const t = i / 20;
    // ogee shoulder: slow start, round finish
    p.push([s0 + (s1 - s0) * t, rn + 0.6 + (r - rn - 0.6) * Math.pow(Math.sin((Math.PI / 2) * t), 1.6)]);
  }
  const b = Math.min(6, r * 0.16);
  p.push([h - b, r], ...roundedBase(h, r, b));
  return p;
}

/** Whiskey flask: bar-top finish, short neck, compact round shoulder, heavy base. */
function bourbonProfile(h: number, r: number): Profile {
  const rn = 11.5;
  const p: Profile = [[0, 13.2], [0.8, 14.2], [5.5, 14.2], [6.8, 12.6], [8, rn], [26, rn]];
  for (let i = 1; i <= 18; i++) {
    const t = i / 18;
    p.push([26 + 30 * t, rn + (r - rn) * Math.sin((Math.PI / 2) * t)]);
  }
  const b = Math.min(5, r * 0.15);
  p.push([h - b, r], ...roundedBase(h, r, b));
  return p;
}

/** RDK01 (500 ml spirits bottle, finish SH 28/14) traced from the factory drawing. */
function rdk01Profile(): Profile {
  const R = 31.4;
  const rn = 13.35;
  const p: Profile = [
    [0, 13.6],
    [0.6, 14.2],
    [1.6, 14.35],
    [12.4, 14.35],
    [13.6, 14.0],
    [15, rn],
    [81.5, rn],
  ];
  const steps = 18;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    p.push([81.5 + 33.5 * t, rn + (R - rn) * Math.sin((Math.PI / 2) * t)]);
  }
  p.push([294, R], [296.2, 31.1], [298.2, 30.4], [299.4, 29.4], [300, 28.2]);
  return p;
}

/** Base thickness, wall thickness and punt depth for rendering. */
export function buildSpec(c: ContainerModel): { baseMm: number; wallMm: number; puntMm: number } {
  const byProduct: Record<SingleProductKind, { baseMm: number; wallMm: number; puntMm: number }> = {
    candle: { baseMm: 10, wallMm: 3, puntMm: 0 },
    diffuser: { baseMm: 9, wallMm: 3, puntMm: 0 },
    bottle: { baseMm: 6, wallMm: 2.4, puntMm: 0 },
    cosmetic: { baseMm: 9, wallMm: 3.8, puntMm: 0 },
    drinkware: { baseMm: 4, wallMm: 1.2, puntMm: 0 },
  };
  return { ...byProduct[c.product], ...c.build } as { baseMm: number; wallMm: number; puntMm: number };
}

export function rimRadius(c: ContainerModel): number {
  return c.profile[0][1];
}
export function maxRadius(c: ContainerModel): number {
  return Math.max(...c.profile.map(([, r]) => r));
}
/** Radius at height y (linear between profile points). */
export function radiusAt(p: Profile, y: number): number {
  if (y <= p[0][0]) return p[0][1];
  for (let i = 1; i < p.length; i++) {
    const [y1, r1] = p[i];
    const [y0, r0] = p[i - 1];
    if (y <= y1) return r0 + ((r1 - r0) * (y - y0)) / Math.max(1e-6, y1 - y0);
  }
  return p[p.length - 1][1];
}
/** Where the body reaches full radius (top of the straight section). */
export function shoulderY(c: ContainerModel): number {
  const r = c.diameterMm / 2;
  const hit = c.profile.find(([, pr]) => pr >= r - 0.05);
  return hit ? hit[0] : 0;
}

// ------------------------------------------------------------------ production limits
/** Printable item size: body Ø 45–160 mm, body length 80–280 mm (closures are removed for printing). */
export const LIMITS = PRINT_LIMITS;
/**
 * Containers kept on the site although they are outside LIMITS.
 * RDK01 (H 300 mm) is the customer's own bottle from factory drawing 568-SH28x14-500;
 * TODO(content): confirm it can be printed (longer fixture) or remove it from CONTAINERS.
 */
export const LIMIT_EXCEPTIONS = ['bottle-rdk01-500'];

export function withinLimits(c: Pick<ContainerModel, 'diameterMm' | 'heightMm'>): boolean {
  const [dMin, dMax] = LIMITS.diameterMm;
  const [hMin, hMax] = LIMITS.lengthMm;
  return c.diameterMm >= dMin && c.diameterMm <= dMax && c.heightMm >= hMin && c.heightMm <= hMax;
}

// ------------------------------------------------------------------ catalogue
const GLASS_CANDLE: MaterialId[] = ['clear', 'amber', 'black', 'white', 'frosted'];
const GLASS_BOTTLE: MaterialId[] = ['clear', 'amber', 'green', 'frosted', 'black'];
const GLASS_COSMETIC: MaterialId[] = ['frosted', 'white', 'amber', 'clear', 'black', 'cobalt'];
const GLASS_DIFFUSER: MaterialId[] = ['clear', 'amber', 'frosted', 'black', 'white'];
const METAL: MaterialId[] = ['steel', 'powder_black', 'powder_white', 'powder_sage', 'powder_navy'];

export const CONTAINERS: ContainerModel[] = [
  // Candles
  {
    id: 'classic-80',
    product: 'candle',
    name: 'Classic 300',
    diameterMm: 80,
    heightMm: 90,
    volumeMl: 300,
    profile: straight(90, 40),
    print: { topMm: 12, heightMm: 62 },
    contents: { kind: 'wax', topMm: 14 },
    materials: GLASS_CANDLE,
    defaultMaterial: 'frosted',
  },
  {
    id: 'tall-70',
    product: 'candle',
    name: 'Tall 330',
    diameterMm: 70,
    heightMm: 110,
    volumeMl: 330,
    profile: straight(110, 35),
    print: { topMm: 12, heightMm: 82 },
    contents: { kind: 'wax', topMm: 16 },
    materials: GLASS_CANDLE,
    defaultMaterial: 'frosted',
  },
  {
    id: 'wide-100',
    product: 'candle',
    name: 'Wide 500',
    diameterMm: 100,
    heightMm: 84,
    volumeMl: 500,
    profile: straight(84, 50),
    print: { topMm: 10, heightMm: 60 },
    contents: { kind: 'wax', topMm: 12 },
    materials: GLASS_CANDLE,
    defaultMaterial: 'frosted',
  },
  {
    id: 'apothecary-85',
    product: 'candle',
    name: 'Apothecary 350',
    diameterMm: 85,
    heightMm: 96,
    volumeMl: 350,
    profile: bottle(96, 42.5, 33, 6, 19, 1.2),
    print: { topMm: 26, heightMm: 56 },
    contents: { kind: 'wax', topMm: 24 },
    closure: { kind: 'lid', heightMm: 16, finish: 'wood' },
    materials: GLASS_CANDLE,
    defaultMaterial: 'amber',
  },
  {
    id: 'grand-120',
    product: 'candle',
    name: 'Grand 900',
    diameterMm: 120,
    heightMm: 104,
    volumeMl: 900,
    profile: straight(104, 60),
    print: { topMm: 12, heightMm: 78 },
    contents: { kind: 'wax', topMm: 16 },
    materials: GLASS_CANDLE,
    defaultMaterial: 'black',
  },

  // Diffusers
  {
    id: 'diffuser-round-100',
    product: 'diffuser',
    name: 'Round 100',
    diameterMm: 62,
    heightMm: 96,
    volumeMl: 100,
    profile: bottle(96, 31, 10, 12, 32),
    print: { topMm: 36, heightMm: 50 },
    contents: { kind: 'liquid', topMm: 48, tint: '#e6cf93' },
    closure: { kind: 'reeds', heightMm: 95, finish: 'bamboo' },
    materials: GLASS_DIFFUSER,
    defaultMaterial: 'clear',
  },
  {
    id: 'diffuser-cyl-200',
    product: 'diffuser',
    name: 'Cylinder 200',
    diameterMm: 66,
    heightMm: 122,
    volumeMl: 200,
    profile: bottle(122, 33, 11, 10, 26),
    print: { topMm: 30, heightMm: 80 },
    contents: { kind: 'liquid', topMm: 50, tint: '#e6cf93' },
    closure: { kind: 'reeds', heightMm: 105, finish: 'bamboo' },
    materials: GLASS_DIFFUSER,
    defaultMaterial: 'clear',
  },
  {
    id: 'diffuser-flask-150',
    product: 'diffuser',
    name: 'Flask 150',
    diameterMm: 58,
    heightMm: 112,
    volumeMl: 150,
    profile: bottle(112, 29, 8.5, 16, 38),
    print: { topMm: 42, heightMm: 58 },
    contents: { kind: 'liquid', topMm: 56, tint: '#e6cf93' },
    closure: { kind: 'reeds', heightMm: 100, finish: 'bamboo' },
    materials: GLASS_DIFFUSER,
    defaultMaterial: 'amber',
  },

  // Bottles & jars
  {
    // From drawing 568-SH28x14-500 (Kostopil glass works, "Futura 500"): H 300, Ø 62.8,
    // neck Ø 26.7, finish SH 28/14 (Ø 28.7 × 15), shoulder 81.5→115, straight body 115→294.
    id: 'bottle-rdk01-500',
    build: { baseMm: 6, wallMm: 2.4, puntMm: 13 },
    product: 'bottle',
    name: 'RDK01',
    diameterMm: 62.8,
    heightMm: 300,
    volumeMl: 500,
    profile: rdk01Profile(),
    print: { topMm: 125, heightMm: 155 },
    contents: { kind: 'none', topMm: 0 },
    materials: ['clear', 'frosted'],
    defaultMaterial: 'clear',
  },
  {
    id: 'bottle-swing-500',
    build: { baseMm: 7, wallMm: 2.8, puntMm: 6 },
    product: 'bottle',
    name: 'Swing 500',
    diameterMm: 72,
    heightMm: 232,
    volumeMl: 500,
    profile: swingProfile(232, 36),
    print: { topMm: 98, heightMm: 118 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'swing', heightMm: 16, finish: 'white' },
    materials: GLASS_BOTTLE,
    defaultMaterial: 'clear',
  },
  {
    id: 'bottle-milk-300',
    build: { baseMm: 5, wallMm: 2.4, puntMm: 4 },
    product: 'bottle',
    name: 'Milk 300',
    diameterMm: 62,
    heightMm: 150,
    volumeMl: 300,
    profile: bottle(150, 31, 19, 18, 52),
    print: { topMm: 60, heightMm: 70 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'cap', heightMm: 12, finish: 'gold' },
    materials: GLASS_BOTTLE,
    defaultMaterial: 'clear',
  },
  {
    id: 'bottle-bourbon-250',
    build: { baseMm: 12, wallMm: 3.2, puntMm: 0 },
    product: 'bottle',
    name: 'Bourbon 250',
    diameterMm: 64,
    heightMm: 150,
    volumeMl: 250,
    profile: bourbonProfile(150, 32),
    print: { topMm: 64, heightMm: 70 },
    contents: { kind: 'liquid', topMm: 50, tint: '#a65a1c' },
    closure: { kind: 'tcork', heightMm: 20, finish: 'wood' },
    materials: GLASS_BOTTLE,
    defaultMaterial: 'clear',
  },
  {
    id: 'jar-mason-500',
    build: { baseMm: 5, wallMm: 2.4 },
    product: 'bottle',
    name: 'Jar 500',
    diameterMm: 86,
    heightMm: 112,
    volumeMl: 500,
    profile: bottle(112, 43, 37, 10, 22, 1.5),
    print: { topMm: 28, heightMm: 62 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'cap', heightMm: 13, finish: 'gold' },
    materials: GLASS_BOTTLE,
    defaultMaterial: 'clear',
  },
  {
    id: 'jar-deli-250',
    build: { baseMm: 5, wallMm: 2.4 },
    product: 'bottle',
    name: 'Jar 250',
    diameterMm: 70,
    heightMm: 86,
    volumeMl: 250,
    profile: bottle(86, 35, 31, 8, 15, 1.2),
    print: { topMm: 20, heightMm: 50 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'cap', heightMm: 11, finish: 'black' },
    materials: GLASS_BOTTLE,
    defaultMaterial: 'clear',
  },

  // Cosmetic packaging (all within Ø 45–160 × 80–280 mm)
  {
    id: 'cosmetic-jar-200',
    build: { baseMm: 14, wallMm: 5 },
    product: 'cosmetic',
    name: 'Cream 200',
    diameterMm: 74,
    heightMm: 82,
    volumeMl: 200,
    profile: bottle(82, 37, 33, 7, 13, 0.8),
    print: { topMm: 17, heightMm: 52 },
    contents: { kind: 'cream', topMm: 14, tint: '#f7f2ea' },
    closure: { kind: 'lid', heightMm: 22, finish: 'white' },
    materials: GLASS_COSMETIC,
    defaultMaterial: 'white',
  },
  {
    id: 'cosmetic-dropper-100',
    build: { baseMm: 7, wallMm: 2.6 },
    product: 'cosmetic',
    name: 'Dropper 100',
    diameterMm: 46,
    heightMm: 112,
    volumeMl: 100,
    profile: bottle(112, 23, 9.5, 11, 30),
    print: { topMm: 34, heightMm: 68 },
    contents: { kind: 'liquid', topMm: 40, tint: '#e9c878' },
    closure: { kind: 'dropper', heightMm: 50, finish: 'gold' },
    materials: GLASS_COSMETIC,
    defaultMaterial: 'amber',
  },
  {
    id: 'cosmetic-toner-200',
    build: { baseMm: 7, wallMm: 2.6 },
    product: 'cosmetic',
    name: 'Toner 200',
    diameterMm: 52,
    heightMm: 150,
    volumeMl: 200,
    profile: bottle(150, 26, 11, 12, 36),
    print: { topMm: 42, heightMm: 96 },
    contents: { kind: 'liquid', topMm: 46, tint: '#efe6ee' },
    closure: { kind: 'cap', heightMm: 18, finish: 'black' },
    materials: GLASS_COSMETIC,
    defaultMaterial: 'frosted',
  },
  {
    id: 'cosmetic-pump-250',
    build: { baseMm: 8, wallMm: 2.8 },
    product: 'cosmetic',
    name: 'Pump 250',
    diameterMm: 56,
    heightMm: 165,
    volumeMl: 250,
    profile: bottle(165, 28, 11, 12, 34),
    print: { topMm: 42, heightMm: 110 },
    contents: { kind: 'liquid', topMm: 48, tint: '#f1ebe0' },
    closure: { kind: 'pump', heightMm: 42, finish: 'white' },
    materials: GLASS_COSMETIC,
    defaultMaterial: 'white',
  },

  // Drinkware: thermoses, sports and water bottles, tumblers — round, no handle
  {
    id: 'drink-thermos-500',
    product: 'drinkware',
    name: 'Thermos 500',
    diameterMm: 70,
    heightMm: 230,
    volumeMl: 500,
    profile: flask(230, 35, 29, 16, 30),
    print: { topMm: 40, heightMm: 170 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'thermo', heightMm: 42, finish: 'silver' },
    materials: METAL,
    defaultMaterial: 'powder_black',
  },
  {
    id: 'drink-thermos-750',
    product: 'drinkware',
    name: 'Thermos 750',
    diameterMm: 80,
    heightMm: 262,
    volumeMl: 750,
    profile: flask(262, 40, 33, 18, 34),
    print: { topMm: 44, heightMm: 196 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'thermo', heightMm: 48, finish: 'silver' },
    materials: METAL,
    defaultMaterial: 'steel',
  },
  {
    id: 'drink-sport-600',
    product: 'drinkware',
    name: 'Sport 600',
    diameterMm: 74,
    heightMm: 205,
    volumeMl: 600,
    profile: flask(205, 37, 24, 12, 40),
    print: { topMm: 50, heightMm: 138 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'sport', heightMm: 36, finish: 'black' },
    materials: METAL,
    defaultMaterial: 'powder_white',
  },
  {
    id: 'drink-tumbler-450',
    product: 'drinkware',
    name: 'Tumbler 450',
    diameterMm: 82,
    heightMm: 160,
    volumeMl: 450,
    profile: flask(160, 41, 39.5, 6, 10),
    print: { topMm: 16, heightMm: 128 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'lid', heightMm: 14, finish: 'black' },
    materials: METAL,
    defaultMaterial: 'powder_sage',
  },
  {
    id: 'drink-glass-500',
    build: { baseMm: 6, wallMm: 2.6, puntMm: 3 },
    product: 'drinkware',
    name: 'Glass Bottle 500',
    diameterMm: 68,
    heightMm: 220,
    volumeMl: 500,
    profile: bottle(220, 34, 22, 14, 42, 1),
    print: { topMm: 52, heightMm: 150 },
    contents: { kind: 'none', topMm: 0 },
    closure: { kind: 'thermo', heightMm: 30, finish: 'bamboo' },
    materials: ['clear', 'frosted'],
    defaultMaterial: 'clear',
  },
];

export const PRODUCTS: SingleProductKind[] = ['candle', 'diffuser', 'bottle', 'cosmetic', 'drinkware'];

/** Containers outside the production limits (should only be LIMIT_EXCEPTIONS). */
export function outOfLimits(): ContainerModel[] {
  return CONTAINERS.filter((c) => !withinLimits(c) && !LIMIT_EXCEPTIONS.includes(c.id));
}
if (import.meta.env?.DEV && outOfLimits().length) {
  console.warn('[catalogue] containers outside print limits:', outOfLimits().map((c) => c.id));
}

export function containersFor(p: SingleProductKind): ContainerModel[] {
  return CONTAINERS.filter((c) => c.product === p);
}
export function getContainer(id: string): ContainerModel {
  return CONTAINERS.find((c) => c.id === id) ?? CONTAINERS[0];
}
export function defaultContainer(p: SingleProductKind): ContainerModel {
  return containersFor(p)[0];
}
export function circumference(c: ContainerModel): number {
  return Math.PI * c.diameterMm;
}

export const INK_SETUPS: InkSetup[] = ['cmyk', 'cmyk_white', 'cmyk_white_varnish'];
export const COVERAGES: Coverage[] = ['single_logo', 'partial', 'full_wrap'];

// ------------------------------------------------------------------ materials
export type MaterialBase = 'clear' | 'tinted' | 'dark' | 'white';

export interface MaterialLook {
  body: string;
  edge: string;
  opacity: number;
  /** How much of the contents shows through, 0…1. */
  contentVisible: number;
  /** Colour seen inside an open container (candles, tumblers). */
  inner: string;
  frosted: boolean;
  base: MaterialBase;
  /** Opaque coated surface (powder coat): no see-through, soft satin reflections. */
  coated?: boolean;
  /** Brushed stainless steel: hard, streaky reflections. */
  metal?: boolean;
}

export const MATERIAL_LOOK: Record<MaterialId, MaterialLook> = {
  clear: { body: '#e3ecea', edge: '#7f9a95', opacity: 0.28, contentVisible: 1, inner: '#e9e2d5', frosted: false, base: 'clear' },
  amber: { body: '#a2561c', edge: '#5e2d0c', opacity: 0.72, contentVisible: 0.55, inner: '#c98a4a', frosted: false, base: 'tinted' },
  green: { body: '#56713a', edge: '#2b3d19', opacity: 0.72, contentVisible: 0.5, inner: '#7d9460', frosted: false, base: 'tinted' },
  cobalt: { body: '#1f4fa0', edge: '#0e2758', opacity: 0.78, contentVisible: 0.4, inner: '#4a6fb3', frosted: false, base: 'tinted' },
  black: { body: '#16181a', edge: '#000000', opacity: 0.97, contentVisible: 0, inner: '#3a3632', frosted: false, base: 'dark' },
  white: { body: '#f4f4f1', edge: '#c9cbc6', opacity: 0.97, contentVisible: 0, inner: '#3a3632', frosted: false, base: 'white' },
  frosted: { body: '#e9eeee', edge: '#b6c2c3', opacity: 0.62, contentVisible: 0.6, inner: '#ece6db', frosted: true, base: 'clear' },
  steel: { body: '#b4babd', edge: '#4f5558', opacity: 1, contentVisible: 0, inner: '#9aa0a3', frosted: false, base: 'tinted', metal: true },
  powder_white: { body: '#f2f1ec', edge: '#c4c2bb', opacity: 1, contentVisible: 0, inner: '#b9bec1', frosted: false, base: 'white', coated: true },
  powder_black: { body: '#232425', edge: '#060606', opacity: 1, contentVisible: 0, inner: '#b9bec1', frosted: false, base: 'dark', coated: true },
  powder_sage: { body: '#9fae96', edge: '#66765f', opacity: 1, contentVisible: 0, inner: '#b9bec1', frosted: false, base: 'tinted', coated: true },
  powder_navy: { body: '#28344d', edge: '#0f1626', opacity: 1, contentVisible: 0, inner: '#b9bec1', frosted: false, base: 'dark', coated: true },
};

/** Ink setup we recommend for a material. */
export function recommendedInk(m: MaterialId): InkSetup {
  return m === 'white' || m === 'powder_white' ? 'cmyk' : 'cmyk_white';
}

/** How ink reads on a material: opaque with a white underbase or on white. */
export function inkOpacity(m: MaterialId, ink: InkSetup): number {
  const base = MATERIAL_LOOK[m].base;
  if (ink !== 'cmyk' || base === 'white') return 1;
  if (base === 'dark') return 0.16;
  if (base === 'tinted') return 0.55;
  return 0.68;
}

export const CLOSURE_COLORS: Record<ClosureFinish, [string, string, string]> = {
  wood: ['#7a5634', '#b8895a', '#5f4127'],
  gold: ['#8a6a2c', '#e4c77c', '#6e521c'],
  silver: ['#7d8386', '#e3e6e7', '#5c6164'],
  black: ['#0d0d0d', '#4a4a4a', '#050505'],
  white: ['#d9d7d1', '#ffffff', '#c3c0b8'],
  cork: ['#9c7447', '#d2ab79', '#86603a'],
  bamboo: ['#a4804f', '#d8b983', '#8a6a3f'],
};
